import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AdminLayout } from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import * as Lucide from "lucide-react";
import { toast } from "sonner";

const sym = (c) => (c === "INR" ? "₹" : "$");

const blankItem = () => ({ name: "", description: "", quantity: 1, unit_price: 0, discount_pct: 0, cgst_pct: 0, sgst_pct: 0, igst_pct: 0 });
const blankCust = () => ({ name: "", email: "", phone: "", address: "", gstin: "" });
const isNewParam = (id) => id === "new";
const freshInv = () => ({
  customer: blankCust(),
  items: [blankItem()],
  currency: "INR",
  issued_at: new Date().toISOString().slice(0, 10),
  due_at: "",
  notes: "",
  terms: "Payment due within 7 days.",
  status: "draft",
  computed: { subtotal: 0, discount_total: 0, cgst_total: 0, sgst_total: 0, igst_total: 0, grand_total: 0, items: [] },
});

const fmt = (n, c) => `${sym(c)}${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AdminInvoiceEdit() {
  const { id } = useParams();
  const nav = useNavigate();
  const isNew = id === "new";
  const [busy, setBusy] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showSignLink, setShowSignLink] = useState(null);
  const [showEmail, setShowEmail] = useState(false);

  const [inv, setInv] = useState(() => isNewParam(id) ? freshInv() : null);
  const [pdfDate, setPdfDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [pdfCustomerSignDate, setPdfCustomerSignDate] = useState(""); // blank = printed underscore line
  const [showPdfDate, setShowPdfDate] = useState(false);

  useEffect(() => {
    if (id === "new") return;
    api.get(`/admin/invoices/${id}`).then((r) => {
      const loaded = {
        ...r.data,
        items: r.data.items_input || [],
        issued_at: r.data.issued_at ? r.data.issued_at.slice(0, 10) : "",
        due_at: r.data.due_at ? r.data.due_at.slice(0, 10) : "",
      };
      setInv(loaded);    }).catch(() => { toast.error("Invoice not found"); nav("/admin/invoices"); });
  }, [id, nav]);

  const liveComputed = useMemo(() => {
    if (!inv) return null;
    const isInr = inv.currency === "INR";
    let subtotal = 0, discount = 0, cgst = 0, sgst = 0, igst = 0;
    const items = (inv.items || []).map((it) => {
      const q = +it.quantity || 0, u = +it.unit_price || 0, dp = +it.discount_pct || 0;
      const gross = q * u;
      const da = (gross * dp) / 100;
      const net = gross - da;
      const cg = isInr ? (net * (+it.cgst_pct || 0)) / 100 : 0;
      const sg = isInr ? (net * (+it.sgst_pct || 0)) / 100 : 0;
      const ig = isInr ? (net * (+it.igst_pct || 0)) / 100 : 0;
      subtotal += gross; discount += da; cgst += cg; sgst += sg; igst += ig;
      return { ...it, line_total: net + cg + sg + ig };
    });
    return { items, subtotal, discount_total: discount, cgst_total: cgst, sgst_total: sgst, igst_total: igst, grand_total: subtotal - discount + cgst + sgst + igst };
  }, [inv]);

  if (!inv || !liveComputed) return <AdminLayout><div className="text-slate-400">Loading…</div></AdminLayout>;
  const isInr = inv.currency === "INR";

  const updItem = (i, patch) => setInv((v) => ({ ...v, items: v.items.map((it, j) => j === i ? { ...it, ...patch } : it) }));
  const addItem = () => setInv((v) => ({ ...v, items: [...v.items, blankItem()] }));
  const rmItem = (i) => setInv((v) => ({ ...v, items: v.items.filter((_, j) => j !== i) }));

  const save = async () => {
    if (!inv.customer?.name?.trim()) { toast.error("Customer name required"); return; }
    if (!inv.items.length) { toast.error("Add at least one item"); return; }
    setBusy(true);
    try {
      const payload = {
        customer: inv.customer,
        items: inv.items,
        currency: inv.currency,
        issued_at: inv.issued_at ? new Date(inv.issued_at).toISOString() : null,
        due_at: inv.due_at ? new Date(inv.due_at).toISOString() : null,
        notes: inv.notes,
        terms: inv.terms,
        invoice_no: (inv.invoice_no || "").trim() || undefined,
      };
      if (isNew) {
        const r = await api.post("/admin/invoices", payload);
        toast.success("Invoice created");
        nav(`/admin/invoices/${r.data.id}`);
      } else {
        const r = await api.patch(`/admin/invoices/${id}`, payload);
        setInv({ ...r.data, items: r.data.items_input || [], issued_at: r.data.issued_at?.slice(0, 10) || "", due_at: r.data.due_at?.slice(0, 10) || "" });
        toast.success("Saved");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const downloadPDF = async (dateOverride, signDateOverride) => {
    const qs = new URLSearchParams();
    if (dateOverride) qs.set("date", new Date(dateOverride).toISOString());
    if (signDateOverride) qs.set("customer_sign_date", new Date(signDateOverride).toISOString());
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    const res = await api.get(`/admin/invoices/${id}/pdf${suffix}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    window.open(url, "_blank");
    setShowPdfDate(false);
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <button onClick={() => nav("/admin/invoices")} className="text-xs text-purple-300 hover:text-purple-200 mb-2"><Lucide.ChevronLeft size={12} className="inline" /> Back to invoices</button>
          <h1 className="bx-heading text-2xl font-extrabold">{isNew ? "New invoice" : inv.invoice_no}</h1>
          {!isNew && <p className="text-xs text-slate-400">Status: <strong className={inv.status === "paid" ? "text-emerald-300" : "text-slate-200"}>{inv.status}</strong></p>}
        </div>
        <div className="flex gap-2 flex-wrap">
          {!isNew && (
            <>
              <button data-testid="inv-record-payment" onClick={() => setShowPayment(true)} className="bx-btn-ghost text-xs"><Lucide.Wallet size={12} /> {inv.payment ? "Update payment" : "Record payment"}</button>
              <button data-testid="inv-sign-link" onClick={async () => { const r = await api.post(`/admin/invoices/${id}/signature-link`); setShowSignLink(r.data.token); }} className="bx-btn-ghost text-xs"><Lucide.PenTool size={12} /> Customer signature link</button>
              <button data-testid="inv-email" onClick={() => setShowEmail(true)} className="bx-btn-ghost text-xs"><Lucide.Mail size={12} /> Email</button>
              <button
                data-testid="inv-whatsapp"
                onClick={() => {
                  const raw = (inv.customer?.phone || "").trim();
                  if (!raw) { toast.error("Add a customer phone number first"); return; }
                  // Strip non-digits; assume India (+91) if 10 digits and no country code.
                  let digits = raw.replace(/\D/g, "");
                  if (digits.length === 10) digits = "91" + digits;
                  const sym = inv.currency === "INR" ? "₹" : "$";
                  const total = Number(inv.computed?.grand_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                  const url = `${window.location.origin}/invoice/view/${inv.public_token}`;
                  const text =
                    `Hi ${inv.customer?.name || "there"}, ` +
                    `please find your invoice ${inv.invoice_no} for ${sym}${total} from BINAREX AND CO.\n\n` +
                    `View / download: ${url}`;
                  window.open(`https://wa.me/${digits}?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
                }}
                className="bx-btn-ghost text-xs"
                style={{ color: "#22C55E" }}
                title="Send via WhatsApp"
              >
                <Lucide.MessageCircle size={12} /> WhatsApp
              </button>
              <button data-testid="inv-pdf" onClick={() => setShowPdfDate(true)} className="bx-btn-ghost text-xs"><Lucide.Download size={12} /> PDF</button>
              <a href={`${window.location.origin}/invoice/view/${inv.public_token}`} target="_blank" rel="noopener noreferrer" className="bx-btn-ghost text-xs"><Lucide.ExternalLink size={12} /> Public view</a>
            </>
          )}
          <button data-testid="inv-save" onClick={save} disabled={busy} className="bx-btn-primary disabled:opacity-50"><Lucide.Save size={14} /> {isNew ? "Create" : "Save"}</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-5">
        {/* Customer + meta */}
        <div className="bx-card p-5 lg:col-span-1">
          <h3 className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-3">Bill to</h3>
          <input data-testid="cust-name" value={inv.customer.name} onChange={(e) => setInv({ ...inv, customer: { ...inv.customer, name: e.target.value } })} placeholder="Customer name *" className="bx-input mb-2" />
          <input value={inv.customer.email || ""} onChange={(e) => setInv({ ...inv, customer: { ...inv.customer, email: e.target.value } })} placeholder="Email" className="bx-input mb-2" />
          <input value={inv.customer.phone || ""} onChange={(e) => setInv({ ...inv, customer: { ...inv.customer, phone: e.target.value } })} placeholder="Phone" className="bx-input mb-2" />
          <textarea rows={3} value={inv.customer.address || ""} onChange={(e) => setInv({ ...inv, customer: { ...inv.customer, address: e.target.value } })} placeholder="Address" className="bx-input mb-2 resize-y" />
          <input value={inv.customer.gstin || ""} onChange={(e) => setInv({ ...inv, customer: { ...inv.customer, gstin: e.target.value } })} placeholder="GSTIN (optional)" className="bx-input bx-mono text-xs" />

          <h3 className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-3 mt-5">Details</h3>
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2">
              <label className="text-[10px] text-slate-500">Invoice No.</label>
              <input data-testid="inv-number" value={inv.invoice_no || ""} onChange={(e) => setInv({ ...inv, invoice_no: e.target.value })} placeholder={isNew ? "Leave blank to auto-generate (BX-YYYYMM-NNNN)" : "Invoice number"} className="bx-input bx-mono text-xs" />
            </div>
            <div>
              <label className="text-[10px] text-slate-500">Currency</label>
              <select data-testid="inv-currency" value={inv.currency} onChange={(e) => setInv({ ...inv, currency: e.target.value })} className="bx-input text-sm">
                <option value="INR">₹ INR</option>
                <option value="USD">$ USD</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] text-slate-500">Issued</label>
              <input type="date" value={inv.issued_at || ""} onChange={(e) => setInv({ ...inv, issued_at: e.target.value })} className="bx-input text-sm" />
            </div>
            <div className="col-span-2">
              <label className="text-[10px] text-slate-500">Due (optional)</label>
              <input type="date" value={inv.due_at || ""} onChange={(e) => setInv({ ...inv, due_at: e.target.value })} className="bx-input text-sm" />
            </div>
          </div>
        </div>

        {/* Items + totals */}
        <div className="bx-card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Line items</h3>
            <button onClick={addItem} className="text-purple-300 hover:text-purple-200 text-xs"><Lucide.Plus size={12} className="inline" /> Add item</button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left pb-2">Item</th>
                  <th className="text-right pb-2 w-16">Qty</th>
                  <th className="text-right pb-2 w-24">Rate</th>
                  <th className="text-right pb-2 w-16">Disc%</th>
                  {isInr && <th className="text-right pb-2 w-16">CGST%</th>}
                  {isInr && <th className="text-right pb-2 w-16">SGST%</th>}
                  {isInr && <th className="text-right pb-2 w-16">IGST%</th>}
                  <th className="text-right pb-2 w-24">Total</th>
                  <th className="w-6"></th>
                </tr>
              </thead>
              <tbody>
                {inv.items.map((it, i) => (
                  <tr key={i} className="border-t border-white/5" data-testid={`item-row-${i}`}>
                    <td className="py-2 pr-2">
                      <input value={it.name} onChange={(e) => updItem(i, { name: e.target.value })} placeholder="Item name *" className="bx-input text-xs py-1.5" />
                      <input value={it.description || ""} onChange={(e) => updItem(i, { description: e.target.value })} placeholder="Description" className="bx-input text-[11px] py-1 mt-1 text-slate-400" />
                    </td>
                    <td className="py-2"><input type="number" step="0.01" min="0" value={it.quantity} onChange={(e) => updItem(i, { quantity: e.target.value })} className="bx-input text-xs py-1.5 text-right bx-mono" /></td>
                    <td className="py-2 pl-1"><input type="number" step="0.01" min="0" value={it.unit_price} onChange={(e) => updItem(i, { unit_price: e.target.value })} className="bx-input text-xs py-1.5 text-right bx-mono" /></td>
                    <td className="py-2 pl-1"><input type="number" step="0.01" min="0" max="100" value={it.discount_pct} onChange={(e) => updItem(i, { discount_pct: e.target.value })} className="bx-input text-xs py-1.5 text-right bx-mono" /></td>
                    {isInr && <td className="py-2 pl-1"><input type="number" step="0.01" min="0" max="50" value={it.cgst_pct} onChange={(e) => updItem(i, { cgst_pct: e.target.value })} className="bx-input text-xs py-1.5 text-right bx-mono" /></td>}
                    {isInr && <td className="py-2 pl-1"><input type="number" step="0.01" min="0" max="50" value={it.sgst_pct} onChange={(e) => updItem(i, { sgst_pct: e.target.value })} className="bx-input text-xs py-1.5 text-right bx-mono" /></td>}
                    {isInr && <td className="py-2 pl-1"><input type="number" step="0.01" min="0" max="50" value={it.igst_pct} onChange={(e) => updItem(i, { igst_pct: e.target.value })} className="bx-input text-xs py-1.5 text-right bx-mono" /></td>}
                    <td className="py-2 pl-1 text-right font-bold text-purple-300 whitespace-nowrap">{fmt(liveComputed.items[i]?.line_total, inv.currency)}</td>
                    <td className="py-2 pl-1 text-right"><button onClick={() => rmItem(i)} className="text-rose-300 hover:text-rose-200"><Lucide.X size={12} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-5 pt-4 border-t border-white/5 grid grid-cols-2 gap-x-8 gap-y-1 text-sm max-w-md ml-auto">
            <span className="text-slate-400">Subtotal</span><span className="text-right bx-mono">{fmt(liveComputed.subtotal, inv.currency)}</span>
            {liveComputed.discount_total > 0 && <><span className="text-slate-400">Discount</span><span className="text-right bx-mono text-rose-300">− {fmt(liveComputed.discount_total, inv.currency)}</span></>}
            {isInr && liveComputed.cgst_total > 0 && <><span className="text-slate-400">CGST</span><span className="text-right bx-mono">{fmt(liveComputed.cgst_total, inv.currency)}</span></>}
            {isInr && liveComputed.sgst_total > 0 && <><span className="text-slate-400">SGST</span><span className="text-right bx-mono">{fmt(liveComputed.sgst_total, inv.currency)}</span></>}
            {isInr && liveComputed.igst_total > 0 && <><span className="text-slate-400">IGST</span><span className="text-right bx-mono">{fmt(liveComputed.igst_total, inv.currency)}</span></>}
            <span className="text-slate-200 font-bold pt-1 border-t border-white/10">Grand total</span>
            <span className="text-right bx-mono text-purple-300 font-extrabold text-lg pt-1 border-t border-white/10" data-testid="inv-grand-total">{fmt(liveComputed.grand_total, inv.currency)}</span>
          </div>

          <div className="grid md:grid-cols-2 gap-3 mt-6">
            <div>
              <label className="text-[10px] text-slate-500">Notes</label>
              <textarea rows={3} value={inv.notes || ""} onChange={(e) => setInv({ ...inv, notes: e.target.value })} className="bx-input text-xs resize-y" />
            </div>
            <div>
              <label className="text-[10px] text-slate-500">Terms</label>
              <textarea rows={3} value={inv.terms || ""} onChange={(e) => setInv({ ...inv, terms: e.target.value })} className="bx-input text-xs resize-y" />
            </div>
          </div>
        </div>
      </div>

      {!isNew && inv.payment && (
        <div className="bx-card p-4 mt-4 border-emerald-500/20" style={{ borderColor: "rgba(16,185,129,0.25)" }}>
          <div className="flex items-center gap-3">
            <Lucide.CheckCircle2 size={20} className="text-emerald-300" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-slate-100">Payment received via {inv.payment.method}</div>
              <div className="text-xs text-slate-400 mt-0.5">
                {fmt(inv.payment.amount, inv.payment.currency)} ·{" "}
                {inv.payment.transaction_ref && <span className="bx-mono text-purple-300">{inv.payment.transaction_ref}</span>} ·{" "}
                {inv.payment.paid_at && new Date(inv.payment.paid_at).toLocaleDateString()}
              </div>
            </div>
            <button onClick={async () => { if (!window.confirm("Clear payment & revert to sent?")) return; await api.delete(`/admin/invoices/${id}/payment`); const r = await api.get(`/admin/invoices/${id}`); setInv({ ...r.data, items: r.data.items_input, issued_at: r.data.issued_at?.slice(0, 10), due_at: r.data.due_at?.slice(0, 10) }); }} className="bx-btn-ghost text-xs text-rose-300"><Lucide.X size={11} /> Clear</button>
          </div>
        </div>
      )}

      {!isNew && (inv.customer_signature_data_url || inv.admin_signature_data_url) && (
        <div className="bx-card p-4 mt-4">
          <h3 className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-3">Signatures</h3>
          <div className="grid grid-cols-2 gap-4">
            <SigPreview label="Customer" sig={inv.customer_signature_data_url} signedAt={inv.customer_signed_at} signerName={inv.customer_signer_name} />
            <SigPreview label="Authorised" sig={inv.admin_signature_data_url} signedAt={inv.admin_signed_at} />
          </div>
        </div>
      )}

      {showPayment && <PaymentModal invoice={inv} onClose={() => setShowPayment(false)} onSaved={(updated) => { setInv({ ...updated, items: updated.items_input, issued_at: updated.issued_at?.slice(0, 10), due_at: updated.due_at?.slice(0, 10) }); setShowPayment(false); }} />}
      {showSignLink && <SignLinkModal token={showSignLink} customerEmail={inv.customer.email} onClose={() => setShowSignLink(null)} />}
      {showEmail && <EmailModal invoice={inv} onClose={() => setShowEmail(false)} />}
      {showPdfDate && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-5">
          <div className="bx-card p-6 max-w-sm w-full">
            <h2 className="bx-heading text-lg font-bold">PDF download options</h2>
            <p className="text-xs text-slate-400 mt-1">Set the dates that appear on the PDF.</p>
            <div className="mt-4 space-y-3">
              <div>
                <label className="text-[11px] text-slate-400">Invoice date <span className="text-slate-600">(header)</span></label>
                <input data-testid="pdf-date" type="date" value={pdfDate} onChange={(e) => setPdfDate(e.target.value)} className="bx-input mt-1" />
              </div>
              <div>
                <label className="text-[11px] text-slate-400">Customer signature date <span className="text-slate-600">(footer — leave blank for customer to handwrite)</span></label>
                <input data-testid="pdf-sign-date" type="date" value={pdfCustomerSignDate} onChange={(e) => setPdfCustomerSignDate(e.target.value)} className="bx-input mt-1" />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setShowPdfDate(false)} className="bx-btn-ghost">Cancel</button>
              <button data-testid="pdf-confirm" onClick={() => downloadPDF(pdfDate, pdfCustomerSignDate)} className="bx-btn-primary"><Lucide.Download size={14} /> Download PDF</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function SigPreview({ label, sig, signedAt, signerName }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      {sig ? (
        <div className="rounded-lg p-3" style={{ background: "white" }}>
          <img src={sig} alt={`${label} signature`} style={{ maxHeight: 60 }} />
        </div>
      ) : <div className="rounded-lg border border-dashed border-white/15 p-6 text-center text-xs text-slate-500">Not signed yet</div>}
      {signerName && <div className="text-xs text-slate-200 mt-1">{signerName}</div>}
      {signedAt && <div className="text-[10px] text-slate-500">Signed: {new Date(signedAt).toLocaleString()}</div>}
    </div>
  );
}

function PaymentModal({ invoice, onClose, onSaved }) {
  const [form, setForm] = useState({ method: invoice.payment?.method || "UPI", transaction_ref: invoice.payment?.transaction_ref || "", amount: invoice.payment?.amount || invoice.computed?.grand_total || 0, currency: invoice.currency, paid_at: (invoice.payment?.paid_at || new Date().toISOString()).slice(0, 16), notes: invoice.payment?.notes || "" });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.amount || form.amount <= 0) { toast.error("Amount required"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/admin/invoices/${invoice.id}/payment`, { ...form, amount: +form.amount, paid_at: new Date(form.paid_at).toISOString() });
      toast.success("Marked PAID — stamp will appear on the PDF");
      onSaved(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-5">
      <div className="bx-card p-6 max-w-md w-full">
        <h2 className="bx-heading text-lg font-bold">Record payment</h2>
        <p className="text-xs text-slate-400 mt-1">Marking this invoice paid will embed a PAID stamp on the PDF.</p>
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="col-span-2">
            <label className="text-xs text-slate-400">Payment method *</label>
            <select data-testid="pay-method" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} className="bx-input mt-1">
              {["UPI", "IMPS", "NEFT", "POS Terminal", "Bank Transfer", "Card", "Cash", "Cheque", "Other"].map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label className="text-xs text-slate-400">Transaction reference</label>
            <input data-testid="pay-ref" value={form.transaction_ref} onChange={(e) => setForm({ ...form, transaction_ref: e.target.value })} placeholder="UTR / Txn ID / RRN…" className="bx-input bx-mono mt-1" />
          </div>
          <div>
            <label className="text-xs text-slate-400">Amount *</label>
            <input data-testid="pay-amount" type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="bx-input bx-mono mt-1" />
          </div>
          <div>
            <label className="text-xs text-slate-400">Currency</label>
            <input value={form.currency} disabled className="bx-input mt-1 opacity-60" />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-slate-400">Paid at</label>
            <input type="datetime-local" value={form.paid_at} onChange={(e) => setForm({ ...form, paid_at: e.target.value })} className="bx-input mt-1" />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-slate-400">Notes (optional)</label>
            <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bx-input resize-y mt-1" />
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="bx-btn-ghost">Cancel</button>
          <button data-testid="pay-save" onClick={save} disabled={busy} className="bx-btn-primary disabled:opacity-50">{busy ? <Lucide.Loader2 className="animate-spin" size={14} /> : <Lucide.CheckCircle2 size={14} />} Mark paid</button>
        </div>
      </div>
    </div>
  );
}

function SignLinkModal({ token, customerEmail, onClose }) {
  const url = `${window.location.origin}/invoice/sign/${token}`;
  const copy = async () => { await navigator.clipboard.writeText(url); toast.success("Link copied"); };
  const waShare = () => {
    const txt = `Please digitally sign your invoice: ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(txt)}`, "_blank", "noopener,noreferrer");
  };
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-5">
      <div className="bx-card p-6 max-w-md w-full">
        <h2 className="bx-heading text-lg font-bold">Customer signature link</h2>
        <p className="text-xs text-slate-400 mt-1">Send this link to {customerEmail || "your customer"}. The link expires in 7 days and works one time. When they sign, the invoice updates automatically.</p>
        <div className="rounded-lg bg-black/40 border border-white/5 p-3 mt-4 bx-mono text-[11px] text-purple-300 break-all" data-testid="sign-link-url">{url}</div>
        <div className="flex gap-2 mt-4 flex-wrap">
          <button onClick={copy} className="bx-btn-primary text-xs"><Lucide.Copy size={12} /> Copy</button>
          <button onClick={waShare} className="bx-btn-ghost text-xs text-emerald-300"><Lucide.MessageCircle size={12} /> WhatsApp</button>
          <a href={url} target="_blank" rel="noopener noreferrer" className="bx-btn-ghost text-xs"><Lucide.ExternalLink size={12} /> Preview</a>
          <button onClick={onClose} className="bx-btn-ghost text-xs ml-auto">Done</button>
        </div>
      </div>
    </div>
  );
}

function EmailModal({ invoice, onClose }) {
  const [to, setTo] = useState(invoice.customer?.email || "");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!to.includes("@")) { toast.error("Valid email please"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/invoices/${invoice.id}/email`, { to: to.trim(), subject: subject.trim() || null, message: message.trim() || null });
      toast.success("Invoice emailed");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-5">
      <div className="bx-card p-6 max-w-md w-full">
        <h2 className="bx-heading text-lg font-bold">Email invoice {invoice.invoice_no}</h2>
        <p className="text-xs text-slate-400 mt-1">PDF is attached automatically. The email also includes a {`"View invoice online"`} button.</p>
        <input data-testid="email-to" value={to} onChange={(e) => setTo(e.target.value)} placeholder="customer@example.com" className="bx-input mt-4" />
        <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={`Subject (default: Invoice ${invoice.invoice_no})`} className="bx-input mt-2" />
        <textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Personal message (optional)" className="bx-input resize-y mt-2" />
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="bx-btn-ghost">Cancel</button>
          <button data-testid="email-send" onClick={send} disabled={busy} className="bx-btn-primary disabled:opacity-50">{busy ? <Lucide.Loader2 className="animate-spin" size={14} /> : <Lucide.Send size={14} />} Send</button>
        </div>
      </div>
    </div>
  );
}
